import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCloudAuth } from '../../context/CloudAuthContext';
import { useBusinessProfile } from '../../contexts/BusinessProfileContext';
import { Building2, Loader2 } from 'lucide-react';

const statusColors = {
  completed: 'bg-green-900/50 text-green-400',
  'in-progress': 'bg-blue-900/50 text-blue-400',
  ringing: 'bg-yellow-900/50 text-yellow-400',
  queued: 'bg-gray-700 text-gray-300',
  failed: 'bg-red-900/50 text-red-400',
  'no-answer': 'bg-orange-900/50 text-orange-400',
  busy: 'bg-orange-900/50 text-orange-400',
};

export default function VoiceCalls() {
  const { fetchApi } = useCloudAuth();
  const { currentProfile, loading: profileLoading } = useBusinessProfile();
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });

  // Reload when business profile changes
  useEffect(() => {
    if (currentProfile?.id) {
      loadCalls();
    }
  }, [currentProfile?.id, meta.page]);

  const loadCalls = async () => {
    if (!currentProfile?.id) return;
    
    setLoading(true);
    try {
      const profileParam = `businessProfileId=${currentProfile.id}`;
      const res = await fetchApi(`/api/cloud/voice/calls?page=${meta.page}&limit=20&${profileParam}`);
      setCalls(res.data);
      setMeta(res.meta);
    } catch (err) {
      console.error('Failed to load calls:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatCost = (cost) => {
    if (!cost || cost === 0) return '$0.00';
    return `$${cost.toFixed(4)}`;
  };

  if (profileLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!currentProfile) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Building2 className="w-12 h-12 text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">No Business Profile Selected</h3>
        <p className="text-gray-400">Select a business profile from the dropdown above to view calls.</p>
      </div>
    );
  }

  if (calls.length === 0) {
    return (
      <div>
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Building2 className="w-5 h-5 text-purple-400" />
            Calls for {currentProfile.name}
          </h2>
        </div>
        <div className="bg-[#111113] border border-gray-800 rounded-lg p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-800 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No calls yet</h3>
          <p className="text-gray-400">Calls will appear here once you start receiving them.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Profile Header */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Building2 className="w-5 h-5 text-purple-400" />
          Calls for {currentProfile.name}
        </h2>
        <p className="text-sm text-gray-400 mt-1">
          Call history and recordings
        </p>
      </div>

      <div className="bg-[#111113] border border-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Call ID</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Direction</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">From</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">To</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Duration</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Cost</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {calls.map((call) => (
              <tr 
                key={call._id} 
                className="hover:bg-gray-800/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/cloud/voice/calls/${call._id}`)}
                data-testid={`call-row-${call._id}`}
              >
                <td className="px-6 py-4">
                  <span className="text-purple-400 font-mono text-sm">
                    {call.callId?.slice(0, 16) || call._id.slice(0, 8)}...
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center gap-1 text-sm ${
                    call.direction === 'inbound' ? 'text-green-400' : 'text-blue-400'
                  }`}>
                    {call.direction === 'inbound' ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    )}
                    {call.direction}
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-300 text-sm">{call.fromNumber}</td>
                <td className="px-6 py-4 text-gray-300 text-sm">{call.toNumber}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[call.status] || 'bg-gray-700 text-gray-300'}`}>
                    {call.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-300 text-sm">{formatDuration(call.durationSec)}</td>
                <td className="px-6 py-4 text-gray-300 text-sm">{formatCost(call.cost || call.totalCost)}</td>
                <td className="px-6 py-4 text-gray-400 text-sm">{formatDate(call.startedAt || call.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {meta.totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-400">
            Showing {calls.length} of {meta.total} calls
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setMeta(m => ({ ...m, page: m.page - 1 }))}
              disabled={meta.page <= 1}
              className="px-3 py-1 bg-gray-800 text-gray-300 rounded disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-gray-400">
              Page {meta.page} of {meta.totalPages}
            </span>
            <button
              onClick={() => setMeta(m => ({ ...m, page: m.page + 1 }))}
              disabled={meta.page >= meta.totalPages}
              className="px-3 py-1 bg-gray-800 text-gray-300 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
